import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { Heart, ShieldCheck, ShieldAlert, FileSignature, Loader2 } from "lucide-react";

const LABELS = {
  ragione_sociale: "Nominativo",
  nome: "Nome",
  cognome: "Cognome",
  email: "Email",
  telefono: "Telefono",
  comune: "Comune",
  anno_nascita: "Anno di nascita",
  come_parte: "Come fa parte della rete",
  ente: "Ente / Associazione",
  ruolo: "Ruolo",
  professione: "Professione",
  ambito: "Ambito",
  preoccupazioni: "Esigenze / Preoccupazioni",
  attivita_rischio: "Attività a rischio",
  note: "Note del cliente",
  contatto_pref: "Contatto preferito",
  orario: "Orario preferito",
  referente: "Referente",
  pec: "PEC",
  indirizzo: "Indirizzo",
  cap: "CAP",
  provincia: "Provincia",
  cluster: "Cluster",
};

const humanize = (k) => LABELS[k] || k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function fmt(v) {
  if (v === true) return "Sì";
  if (v === false) return "No";
  if (Array.isArray(v)) return v.length ? v.join(", ") : null;
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "object") return null; // nested objects handled separately
  return String(v);
}

export function RequestDetailDialog({ request, open, onOpenChange, onTrasforma, converting }) {
  if (!request) return null;
  const d = request.dati_cliente || {};
  const conv = request.convenzione || null;
  const consensi = request.consensi || null;
  const appVerificata = request.appartenenza_verificata;

  const entries = Object.entries(d)
    .filter(([k]) => !["quotazione", "sinistri", "idd"].includes(k))
    .map(([k, v]) => [k, fmt(v)])
    .filter(([, v]) => v !== null);

  const isReteSolidale = request.canale === "rete_solidale";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="request-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-forest">
            {isReteSolidale && <Heart className="h-5 w-5 text-terracotta" />}
            {d.ragione_sociale || `${d.nome || ""} ${d.cognome || ""}`.trim() || "Dettaglio richiesta"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            {request.codice_pratica && (
              <span className="rounded-full bg-forest/10 px-2.5 py-0.5 font-mono text-xs text-forest" data-testid="detail-codice-pratica">
                {request.codice_pratica}
              </span>
            )}
            <Badge variant="secondary">{request.canale}</Badge>
            {request.stato && <StatusBadge state={request.stato} />}
          </div>

          {conv && (
            <div className="rounded-xl border border-terracotta/30 bg-[#FBF4F0] p-4" data-testid="detail-convenzione">
              <p className="mb-2 flex items-center gap-2 font-semibold text-forest">
                <Heart className="h-4 w-4 text-terracotta" /> Rete Solidale — Convenzione
              </p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                <Field label="Codice convenzione" value={conv.codice} mono />
                <Field label="Ente promotore" value={conv.ente_promotore} />
                <Field label="Prodotto richiesto" value={request.product_code} />
                <Field label="Ramo" value={conv.ramo} />
                <Field label="Operatore (link)" value={conv.operatore} />
                <Field label="Convenzione valida" value={conv.valida === false ? "No (richiesta ordinaria)" : "Sì"} />
              </div>
            </div>
          )}

          {isReteSolidale && (
            <div
              className={`flex items-start gap-3 rounded-xl border p-3.5 ${
                appVerificata ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"
              }`}
              data-testid="detail-appartenenza"
            >
              {appVerificata ? (
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
              ) : (
                <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              )}
              <div>
                <p className="font-medium text-forest">
                  Appartenenza {appVerificata ? "verificata" : "NON ancora verificata"}
                </p>
                <p className="text-xs text-muted-foreground">
                  Autodichiarazione del cliente: {consensi?.appartenenza ? "Sì" : "No"}. La prova va acquisita e
                  archiviata nel fascicolo <b>prima dell'emissione</b>.
                </p>
              </div>
            </div>
          )}

          <div>
            <p className="mb-2 font-semibold text-forest">Dati inseriti dal cliente</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
              {entries.length === 0 ? (
                <p className="col-span-2 text-muted-foreground">Nessun dato disponibile.</p>
              ) : (
                entries.map(([k, v]) => <Field key={k} label={humanize(k)} value={v} />)
              )}
            </div>
          </div>

          {consensi && (
            <div className="rounded-lg bg-[#F0F4F1] p-3 text-xs text-muted-foreground" data-testid="detail-consensi">
              <b className="text-forest">Consensi:</b> Privacy {consensi.privacy ? "✓" : "✗"} · Marketing{" "}
              {consensi.marketing ? "✓" : "✗"}
              {"appartenenza" in consensi && <> · Autodichiarazione appartenenza {consensi.appartenenza ? "✓" : "✗"}</>}
            </div>
          )}
        </div>

        {isReteSolidale && onTrasforma && (
          <DialogFooter className="mt-2">
            {request.quote_id ? (
              <p className="text-xs text-muted-foreground">Preventivo già creato da questo lead.</p>
            ) : (
              <Button
                onClick={() => onTrasforma(request.id)}
                disabled={converting === request.id}
                data-testid="detail-trasforma-rete"
                className="gap-2 bg-terracotta hover:bg-terracotta/90"
              >
                {converting === request.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSignature className="h-4 w-4" />}
                Trasforma in preventivo Infortuni
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, mono }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`text-forest ${mono ? "font-mono" : ""}`}>{value}</p>
    </div>
  );
}
